/**
 * Copyright (c) Mayoral Moda Infantil, S.A.U. and affiliates.
 */

import { ApolloProvider, NormalizedCacheObject } from '@apollo/client';
import { CustomerWrapper } from '@components/contexts/CustomerContext';
import { PageWrapper } from '@components/contexts/PageContext';
import { RouterProgress } from '@components/ui/Progress';
import createCache, { EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { useApollo } from '@graphql/client';
import { WithLayoutProps } from '@hocs/withLayout';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { NextPage } from 'next';
import { appWithTranslation, SSRConfig, useTranslation } from 'next-i18next';
import { DefaultSeo } from 'next-seo';
import { AppProps } from 'next/app';
import { useEffect } from 'react';

import { NotificationWrapper } from '../components/ui/Notification';

import config from '@/config';
import { Brands } from '@/lib/constants';
import theme from '@/theme';

// Shared emotion styles cache
export const sharedEmotionCache = createCache({ key: 'css', prepend: true });

export interface AppPageProps extends SSRConfig {
  brand: Brands;
  initialApolloState: NormalizedCacheObject | null;
}

export interface MyAppProps extends AppProps<AppPageProps> {
  Component: NextPage<AppPageProps> & WithLayoutProps;
  emotionCache?: EmotionCache;
}

function MyApp({ Component, pageProps, emotionCache = sharedEmotionCache }: MyAppProps) {
  const { brand } = pageProps;
  const { i18n } = useTranslation();
  const apolloClient = useApollo(pageProps.initialApolloState);

  const getLayout: WithLayoutProps['getLayout'] = Component.getLayout ?? ((page) => page);

  useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles && jssStyles.parentElement) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
  }, []);

  return (
    <CacheProvider value={emotionCache}>
      <ApolloProvider client={apolloClient}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
          {/* SEO tags */}
          <DefaultSeo {...config.public.seo[brand]} />

          <ThemeProvider theme={theme}>
            {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
            <CssBaseline />

            <RouterProgress />

            <CustomerWrapper>
              <NotificationWrapper>
                <PageWrapper>{getLayout(<Component {...pageProps} />)}</PageWrapper>
              </NotificationWrapper>
            </CustomerWrapper>
          </ThemeProvider>
        </LocalizationProvider>
      </ApolloProvider>
    </CacheProvider>
  );
}

export default appWithTranslation(MyApp);
